import {useRef, useState} from 'react';
import cn from 'classnames';

import {openExternalWebview} from '@zzng/common/scheme/web';
import {TrackInterface} from '@zzng/common/utils/tiara';

import Space from '@/shared/components/Space';
import {TIARA_ACTION, TiaraAction} from '@/shared/constants/tiara';
import {
  BIZ_COMM_URL,
  KAKAO_CORP_URL,
  MANAGEMENT_POLICY_URL,
  PRIVACY_POLICY_URL,
  PROPOSITION_URL,
  TERMS_URL
} from '@/shared/constants/url';

export type FooterLabel = '이용약관' | '개인정보처리방침' | '운영정책' | '제휴제안';

type MenuItme = {label: FooterLabel; actionName: TiaraAction; url: string; isBold?: boolean};

const MENU_ITEMS = [
  {label: '이용약관', actionName: TIARA_ACTION.TERMS_CLICK, url: TERMS_URL},
  {label: '개인정보처리방침', actionName: TIARA_ACTION.PRIVACY_POLICY_CLICK, url: PRIVACY_POLICY_URL, isBold: true},
  {label: '운영정책', actionName: TIARA_ACTION.MANAGEMENT_POLICY_CLICK, url: MANAGEMENT_POLICY_URL},
  {label: '제휴제안', actionName: TIARA_ACTION.PROPOSITION_CLICK, url: PROPOSITION_URL}
] satisfies MenuItme[];

type Props = {
  invisibleLabels?: FooterLabel[];
  trackEvent: (tiara: TrackInterface) => void;
};

const DefaultFooter = ({invisibleLabels, trackEvent}: Props) => {
  const [open, setOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const onClickLink = (url: string) => {
    if (window.digitalCard) {
      openExternalWebview(url, {isCurrentWebviewClose: false});
    } else {
      window.open(url, '_blank');
    }
  };

  const onClickBusiness = () => {
    trackEvent({action: TIARA_ACTION.BUSINESS_INFO_CLICK});
    setOpen(!open);
    // 사업자 정보 영역 렌더링 후 최하단까지 스크롤
    setTimeout(() => bottomRef.current?.scrollIntoView());
  };

  const onClickBusinessConfirm = () => {
    trackEvent({action: TIARA_ACTION.BUSINESS_INFO_CONFIRM_CLICK});
    onClickLink(BIZ_COMM_URL);
  };

  return (
    <div className="doc-footer type2" role="contentinfo">
      <h2 className="screen_out">서비스 이용정보</h2>
      <div className="inner_g">
        <div className="menu_footer">
          {MENU_ITEMS.filter((item) => !invisibleLabels?.includes(item.label)).map((item) => (
            <button
              key={item.label}
              type="button"
              className="btn_menu"
              onClick={() => {
                trackEvent({action: item.actionName});
                onClickLink(item.url);
              }}
            >
              {item.isBold ? <em className="emph_g">{item.label}</em> : item.label}
              <span className="screen_out">(새창열림)</span>
            </button>
          ))}
        </div>
        <div className={cn('info_footer', {open})}>
          <button type="button" className="btn_copy" onClick={() => onClickLink(KAKAO_CORP_URL)}>
            <span className="icon icon_copyright">ⓒ</span> Kakao Corp.
            <span className="screen_out">(새창열림)</span>
          </button>
          <button
            type="button"
            id="btnInfoFooter"
            className="btn_info"
            aria-expanded={open}
            aria-controls="infoFooter"
            onClick={onClickBusiness}
          >
            사업자정보
            <span className="icon icon_arr_down_type3" aria-expanded={open}>
              더보기
            </span>
          </button>
          <div className="wrap_info" role="region" aria-labelledby="btnInfoFooter">
            <dl className="list_info" role="text">
              <dt>대표이사</dt>
              <dd>정신아</dd>
            </dl>
            <dl className="list_info" role="text">
              <dt>주소</dt>
              <dd>제주특별자치도 제주시 첨단로 242(영평동)</dd>
            </dl>
            <dl className="list_info" role="text">
              <dt>사업자등록번호</dt>
              <dd>
                120-81-47521
                <Space />
                <button
                  type="button"
                  className="link_info"
                  aria-label="공정거래위원회 사업자등록번호 확인 사이트(새창열림) 버튼"
                  onClick={onClickBusinessConfirm}
                >
                  확인
                </button>
              </dd>
            </dl>
            <dl className="list_info" role="text">
              <dt>통신판매업신고번호</dt>
              <dd>제2015-제주아라-0032호</dd>
            </dl>
            <dl className="list_info" role="text">
              <dt>호스팅사업자</dt>
              <dd>주식회사 카카오</dd>
            </dl>
            <dl className="list_info type2" role="text">
              <dt>고객센터</dt>
              <dd>1577-3754</dd>
            </dl>
            <dl className="list_info" role="text">
              <dt>메일</dt>
              <dd>help.notice@kakaocorp.com</dd>
            </dl>
          </div>
        </div>
      </div>
      <div className="screen_out" ref={bottomRef}></div>
    </div>
  );
};

export default DefaultFooter;
